// utils/bookingDraft.js
// Booking flow local persistence with evidence-driven validation.
// Original IP — LoadDash Services.

const DRAFT_KEY = "loaddash_booking_draft_v1";

const DRAFT_FIELDS = [
  "step", "loadType", "addr", "unit", "notes", "phone",
  "loadSize", "pickDate", "pickWin", "zone", "isQP",
  "payMethod", "placement",
  // Step 1 evidence
  "photoHash", "analysisStatus", "suggestedTier", "suggestedLEU",
  "visionConfidence", "sourceType",
  // Photo gate state
  "photoGate", "photoGateReason",
  // Step 2 / override tracking
  "customerSelectedTier", "tierOverrideDirection",
  // Tier 1 session gating
  "captureSessionId", "confirmedLEU", "systemAssignedTier",
  "classificationConfirmed", "pricingUnlocked", "imageCount",
  "compositeConfidenceScore", "confidenceThreshold",
  "confidenceGatePassed", "sessionValid",
  "scaleReferenceDetected", "calibrationFactor",
  "classificationConsistency", "failureReasons",
];

/**
 * Compute a lightweight hash from image data for evidence identity.
 */
export function computePhotoHash(dataStr) {
  if (!dataStr) return null;
  const sample = dataStr.slice(0, 2000);
  let hash = 5381;
  for (let i = 0; i < sample.length; i++) {
    hash = ((hash << 5) + hash + sample.charCodeAt(i)) & 0xffffffff;
  }
  return "ph_" + Math.abs(hash).toString(36);
}

/**
 * Save current booking state to localStorage.
 */
export function saveDraft(state) {
  try {
    const draft = {};
    let hasData = false;
    for (const key of DRAFT_FIELDS) {
      if (state[key] !== undefined && state[key] !== "" && state[key] !== null) {
        draft[key] = state[key];
        if (key !== "step" && key !== "loadSize" && key !== "placement") hasData = true;
      }
    }
    draft.step = state.step || 1;
    draft.loadSize = state.loadSize || null;
    draft._savedAt = new Date().toISOString();
    if (hasData || (state.step && state.step > 1)) {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      console.debug("[Draft] saved", { step: draft.step, fields: Object.keys(draft).length });
    }
  } catch { /* non-fatal */ }
}

/**
 * Load a saved booking draft from localStorage.
 */
export function loadDraft() {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const draft = JSON.parse(raw);
    if (!draft || typeof draft !== "object") return null;
    if (draft._savedAt) {
      const age = Date.now() - new Date(draft._savedAt).getTime();
      if (age > 24 * 60 * 60 * 1000) { clearDraft(); return null; }
    }
    console.debug("[Draft] loaded", { step: draft.step, fields: Object.keys(draft).length });
    return draft;
  } catch { clearDraft(); return null; }
}

// ── Step validators ──────────────────────────────

/**
 * Step 1 valid = durable photo evidence + successful analysis + not rejected by gate.
 * Rejected Step 1 must not count as valid restored progress.
 */
function isStep1Valid(d) {
  // Tier 1: require session validity, not just photo+analysis success
  if (d.sessionValid === true && d.confirmedLEU && d.systemAssignedTier) return true;
  // Legacy fallback for old drafts: if no sessionValid field exists, use old logic
  if (d.sessionValid === undefined && d.photoHash && d.analysisStatus === "success" && d.suggestedTier && d.photoGate !== "rejected") return true;
  return false;
}

/**
 * Step 2 valid = loadType selected + tier decision made.
 * The customer must have both chosen an item type AND accepted/overridden the tier.
 * suggestedTier proves the recommendation existed.
 * customerSelectedTier proves the customer made a decision.
 */
function isStep2Valid(d) {
  if (!d.loadType) return false;
  if (!d.suggestedTier) return false;
  if (!d.customerSelectedTier) return false;
  // If customer selected a tier, override direction should exist
  if (!d.tierOverrideDirection) return false;
  return true;
}

/**
 * Determine the highest step the user legitimately earned.
 * Each step gate is explicit — no inference from later fields.
 */
export function getMaxValidStep(draft) {
  if (!draft) return 1;

  if (!isStep1Valid(draft)) return 1;

  // Step 1 passed → eligible for Step 2
  if (!isStep2Valid(draft)) return 2;

  // Step 2 passed → eligible for Step 3
  const hasAddr = !!draft.addr && draft.addr.trim().length > 0;
  const hasPhone = !!draft.phone && draft.phone.replace(/\D/g, "").length === 10;
  if (!hasAddr || !hasPhone) return 3;

  // Step 3 passed → eligible for Step 4
  if (!draft.loadSize) return 4;

  // Step 4 passed → eligible for Step 5
  if (!draft.pickDate || !draft.pickWin || !draft.zone) return 5;

  // Step 5 passed → eligible for Step 6
  return 6;
}

/**
 * Clear the saved booking draft.
 */
export function clearDraft() {
  try { localStorage.removeItem(DRAFT_KEY); console.debug("[Draft] cleared"); } catch { /* non-fatal */ }
}
